import React, { useEffect, useState, useCallback, useRef } from "react";
import { message } from "antd";
import { useCart } from "../../../../Context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { DeleteDraftCart } from "../../../../Api";
import {
  useActivePaymentMethodsQuery,
  useActivePaymentStatusesQuery,
  useActiveOrderStatusesQuery,
  useOrderByIdQuery,
  useActiveBankAccountsQuery,
  useEmployeesQuery,
  useCustomersQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
} from "../../../../Services";
import { useCustomerCreditBalanceQuery } from "../../../../Services/credit_service";
import { useKeyboardShortcuts } from "../../../../Hook";
import { SHORTCUTS } from "../../../../Constant";
import {
  PaymentMethod,
  getPaymentMethodCode,
  SellingType,
  PaymentStatus,
  OrderStatus,
  getOrderStatusCode,
  getPaymentStatusCode,
} from "../../../../Enums";
import dayjs from "dayjs";

export interface ChequeInput {
  id: string;
  cheque_no: string;
  bank_name: string;
  cheque_date: string;
  amount: number;
  bank_account_id: string;
}

interface UseCashSaleCheckoutProps {
  open: boolean;
  onClose: () => void;
  onCheckoutSuccess?: (orderId: string) => void;
}

export const useCashSaleCheckout = ({
  open,
  onClose,
  onCheckoutSuccess,
}: UseCashSaleCheckoutProps) => {
  const {
    cart,
    cartTotal,
    customer,
    setCustomer,
    table,
    clearCart,
    isWholeSale,
    editingOrderId,
    exchangeDiscount,
    editingDraftId,
    repairJobId,
    onlineOrderId,
  } = useCart();

  // Drawer states
  const [discount, setDiscount] = useState<number>(0);
  const [otherCharges, setOtherCharges] = useState<number>(0);
  const [employeeId, setEmployeeId] = useState<string>("");

  // Payment states
  const [cashAmount, setCashAmount] = useState<number>(0);
  const [cardAmount, setCardAmount] = useState<number>(0);
  const [cardTxNo, setCardTxNo] = useState<string>("");
  const [cardBankAccountId, setCardBankAccountId] = useState<string>("");

  const [transferAmount, setTransferAmount] = useState<number>(0);
  const [transferTxNo, setTransferTxNo] = useState<string>("");
  const [transferBankAccountId, setTransferBankAccountId] =
    useState<string>("");

  const [cheques, setCheques] = useState<ChequeInput[]>([]);
  const [paymentMode, setPaymentMode] = useState<string>(
    getPaymentMethodCode(PaymentMethod.CASH),
  );
  const [isCreditSale, setIsCreditSale] = useState<boolean>(false);

  // Autofocus input refs
  const cashAmountRef = useRef<any>(null);
  const cardAmountRef = useRef<any>(null);
  const transferAmountRef = useRef<any>(null);
  const chequeAmountRef = useRef<any>(null);
  const lastEnterTimeRef = useRef<number>(0);

  // Metadata Queries using service hooks
  const { data: paymentMethods = [], isLoading: loadingPaymentMethods } =
    useActivePaymentMethodsQuery({ enabled: open });

  const { data: paymentStatuses = [], isLoading: loadingPaymentStatuses } =
    useActivePaymentStatusesQuery({ enabled: open });

  const { data: orderStatuses = [], isLoading: loadingOrderStatuses } =
    useActiveOrderStatusesQuery({ enabled: open });

  const { data: orderDetailsData, isLoading: loadingOrderDetails } =
    useOrderByIdQuery(editingOrderId!, { enabled: open && !!editingOrderId });

  const { data: bankAccounts = [], isLoading: loadingBankAccounts } =
    useActiveBankAccountsQuery({ enabled: open });

  const { data: employeesData, isLoading: loadingEmployees } =
    useEmployeesQuery({ page: 1, limit: 100 }, { enabled: open });
  const employees = employeesData?.data || [];

  const { data: customerResponse, isLoading: loadingCustomers } =
    useCustomersQuery({ enabled: open });

  const customers = customerResponse?.data ?? [];

  // Fetch the selected customer's current outstanding credit balance
  const { data: customerBalanceData } = useCustomerCreditBalanceQuery(
    String(customer?.id ?? ""),
    {
      enabled: open && !!customer?.id && (customer?.credit_limit ?? 0) > 0,
      staleTime: 0,
    },
  );

  // Derived calculations
  const grandTotal = Math.max(
    0,
    cartTotal + otherCharges - discount - exchangeDiscount,
  );

  const totalPaidAmount = isCreditSale
    ? 0
    : cashAmount +
      cardAmount +
      transferAmount +
      cheques.reduce((sum, ch) => sum + ch.amount, 0);

  const balance = Math.max(0, totalPaidAmount - grandTotal);
  const dueAmount = Math.max(0, grandTotal - totalPaidAmount);
  const isPaid = totalPaidAmount >= grandTotal;

  // Hydration state
  const isEditing = useRef(false);

  // Keep the active payment amount in sync with grand total when grand total changes (new sale only)
  useEffect(() => {
    if (open && !isEditing.current && !isCreditSale) {
      const payableToday = Math.max(0, grandTotal);
      if (paymentMode === getPaymentMethodCode(PaymentMethod.CASH)) {
        setCashAmount(payableToday);
      } else if (paymentMode === getPaymentMethodCode(PaymentMethod.CARD)) {
        setCardAmount(payableToday);
      } else if (
        paymentMode === getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)
      ) {
        setTransferAmount(payableToday);
      } else if (paymentMode === getPaymentMethodCode(PaymentMethod.CHEQUE)) {
        setCheques((prev) => {
          if (prev.length === 1) {
            return [{ ...prev[0], amount: payableToday }];
          }
          return prev;
        });
      }
    }
  }, [grandTotal, paymentMode, open, isCreditSale]);

  // Handle reset
  const handleReset = useCallback(() => {
    isEditing.current = false;
    setDiscount(0);
    setOtherCharges(0);
    setEmployeeId("");
    setCashAmount(0);
    setCardAmount(0);
    setCardTxNo("");
    setCardBankAccountId("");
    setTransferAmount(0);
    setTransferTxNo("");
    setTransferBankAccountId("");
    setCheques([]);
    setPaymentMode(getPaymentMethodCode(PaymentMethod.CASH));
    setIsCreditSale(false);
  }, []);

  // Sync paid amount with grand total when drawer opens (new sale only)
  useEffect(() => {
    if (open && !editingOrderId) {
      handleReset();
    }
  }, [open, editingOrderId, handleReset]);

  // Pre-populate fields if editing an existing order
  useEffect(() => {
    if (
      open &&
      editingOrderId &&
      orderDetailsData &&
      paymentMethods.length > 0
    ) {
      const order = orderDetailsData;
      setDiscount(order.discount_amount || 0);
      setOtherCharges(order.other_charges || 0);

      const payments = order.payments || [];
      let cashAmt = 0;
      let cardAmt = 0;
      let cardTx = "";
      let cardBank = "";
      let transferAmt = 0;
      let transferTx = "";
      let transferBank = "";
      const chequeInputs: ChequeInput[] = [];

      payments.forEach((pay: any) => {
        const pm = paymentMethods.find(
          (p: any) => p.id === pay.payment_method_id,
        );
        const code = pm ? pm.code : pay.payment_method_code || "";

        if (code === getPaymentMethodCode(PaymentMethod.CASH)) {
          cashAmt = pay.amount;
        } else if (code === getPaymentMethodCode(PaymentMethod.CARD)) {
          cardAmt = pay.amount;
          cardTx = pay.transaction_no || "";
          cardBank = pay.bank_account_id ? String(pay.bank_account_id) : "";
        } else if (code === getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)) {
          transferAmt = pay.amount;
          transferTx = pay.transaction_no || "";
          transferBank = pay.bank_account_id ? String(pay.bank_account_id) : "";
        } else if (code === getPaymentMethodCode(PaymentMethod.CHEQUE)) {
          chequeInputs.push({
            id: pay.id || String(Math.random()),
            cheque_no: pay.cheque_no || "",
            bank_name: pay.bank_name || "",
            cheque_date: pay.cheque_date ? pay.cheque_date.split(" ")[0] : "",
            amount: pay.amount,
            bank_account_id: pay.bank_account_id
              ? String(pay.bank_account_id)
              : "",
          });
        }
      });

      // Determine payment mode based on populated active payments
      let activeCount = 0;
      let singleMode = getPaymentMethodCode(PaymentMethod.CASH);

      if (cashAmt > 0) {
        activeCount++;
        singleMode = getPaymentMethodCode(PaymentMethod.CASH);
      }
      if (cardAmt > 0) {
        activeCount++;
        singleMode = getPaymentMethodCode(PaymentMethod.CARD);
      }
      if (transferAmt > 0) {
        activeCount++;
        singleMode = getPaymentMethodCode(PaymentMethod.BANK_TRANSFER);
      }
      if (chequeInputs.length > 0) {
        activeCount += chequeInputs.length;
        singleMode = getPaymentMethodCode(PaymentMethod.CHEQUE);
      }

      let prepopulatedPaymentMode = getPaymentMethodCode(PaymentMethod.CASH);
      let prepopulatedIsCreditSale = false;

      if (activeCount > 1) {
        prepopulatedPaymentMode = "split";
        prepopulatedIsCreditSale = false;
      } else {
        prepopulatedPaymentMode = singleMode;
        if (order.grand_total > 0 && order.paid_amount === 0) {
          prepopulatedIsCreditSale = true;
        } else {
          prepopulatedIsCreditSale = false;
        }
      }

      isEditing.current = true;
      setCashAmount(cashAmt);
      setCardAmount(cardAmt);
      setCardTxNo(cardTx);
      setCardBankAccountId(cardBank);
      setTransferAmount(transferAmt);
      setTransferTxNo(transferTx);
      setTransferBankAccountId(transferBank);
      setCheques(chequeInputs);
      setPaymentMode(prepopulatedPaymentMode);
      setIsCreditSale(prepopulatedIsCreditSale);

      if (order.employee_id) {
        setEmployeeId(String(order.employee_id));
      }
    }
  }, [open, editingOrderId, orderDetailsData, paymentMethods]);

  const handleCreditSaleChange = (checked: boolean) => {
    setIsCreditSale(checked);
    if (checked) {
      setCashAmount(0);
      setCardAmount(0);
      setCardTxNo("");
      setCardBankAccountId("");
      setTransferAmount(0);
      setTransferTxNo("");
      setTransferBankAccountId("");
      setCheques([]);
    } else {
      setCashAmount(grandTotal);
      setPaymentMode(getPaymentMethodCode(PaymentMethod.CASH));
    }
  };

  const handlePaymentModeChange = (mode: string) => {
    setPaymentMode(mode);
    setCashAmount(0);
    setCardAmount(0);
    setCardTxNo("");
    setCardBankAccountId("");
    setTransferAmount(0);
    setTransferTxNo("");
    setTransferBankAccountId("");
    setCheques([]);

    if (mode === getPaymentMethodCode(PaymentMethod.CASH)) {
      setCashAmount(grandTotal);
    } else if (mode === getPaymentMethodCode(PaymentMethod.CARD)) {
      setCardAmount(grandTotal);
    } else if (mode === getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)) {
      setTransferAmount(grandTotal);
    } else if (mode === getPaymentMethodCode(PaymentMethod.CHEQUE)) {
      setCheques([
        {
          id: String(Math.random()),
          cheque_no: "",
          bank_name: "",
          cheque_date: dayjs().format("YYYY-MM-DD"),
          amount: grandTotal,
          bank_account_id: "",
        },
      ]);
    } else if (mode === "split") {
      setCashAmount(grandTotal);
    }
  };

  // Keyboard shortcut definitions for drawer cycling and toggle credit
  useKeyboardShortcuts(
    [
      {
        shortcut: SHORTCUTS.CHECKOUT.CYCLE_PAYMENT,
        options: {
          enabled: open,
          preventDefault: true,
          stopPropagation: true,
        },
        handler: (e) => {
          const activeEl = document.activeElement;
          if (
            activeEl instanceof HTMLInputElement ||
            activeEl instanceof HTMLTextAreaElement
          ) {
            const type = activeEl.type.toLowerCase();
            const isNumberInput = activeEl.classList.contains(
              "ant-input-number-input",
            );
            if (
              !isNumberInput &&
              (type === "text" ||
                type === "search" ||
                activeEl.tagName.toLowerCase() === "textarea")
            ) {
              return;
            }
          }
          const PAYMENT_MODES = [
            getPaymentMethodCode(PaymentMethod.CASH),
            getPaymentMethodCode(PaymentMethod.CARD),
            getPaymentMethodCode(PaymentMethod.BANK_TRANSFER),
            getPaymentMethodCode(PaymentMethod.CHEQUE),
            "split",
          ];
          const currentIndex = PAYMENT_MODES.indexOf(paymentMode);
          const nextIndex = (currentIndex + 1) % PAYMENT_MODES.length;
          handlePaymentModeChange(PAYMENT_MODES[nextIndex]);
        },
      },
      {
        shortcut: SHORTCUTS.CHECKOUT.TOGGLE_CREDIT,
        options: {
          enabled: open,
          preventDefault: true,
          stopPropagation: true,
        },
        handler: (e) => {
          handleCreditSaleChange(!isCreditSale);
        },
      },
    ],
    { enabled: open },
  );

  // Auto focus active payment mode amount input
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      if (paymentMode === "cash" || paymentMode === "split") {
        cashAmountRef.current?.focus();
        cashAmountRef.current?.select?.();
      } else if (paymentMode === "card") {
        cardAmountRef.current?.focus();
        cardAmountRef.current?.select?.();
      } else if (paymentMode === "bank_transfer") {
        transferAmountRef.current?.focus();
        transferAmountRef.current?.select?.();
      } else if (paymentMode === "cheque") {
        chequeAmountRef.current?.focus();
        chequeAmountRef.current?.select?.();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [paymentMode, open]);

  // Checkout mutations from order service
  const createOrderMutation = useCreateOrderMutation();
  const updateOrderMutation = useUpdateOrderMutation();

  const checkoutMutation = {
    isPending: createOrderMutation.isPending || updateOrderMutation.isPending,
  };

  const validate = () => {
    if (isCreditSale) return true;

    if (cardAmount > 0 && !cardBankAccountId) {
      message.warning("Please select a bank account for card payment.");
      return false;
    }

    if (transferAmount > 0 && !transferBankAccountId) {
      message.warning("Please select a bank account for bank transfer.");
      return false;
    }

    for (let i = 0; i < cheques.length; i++) {
      const ch = cheques[i];
      if (ch.amount <= 0) {
        message.warning(`Cheque #${i + 1} amount must be greater than 0.`);
        return false;
      }
      if (!ch.cheque_no.trim()) {
        message.warning(`Please enter cheque number for Cheque #${i + 1}.`);
        return false;
      }
      if (!ch.bank_name.trim()) {
        message.warning(`Please enter bank name for Cheque #${i + 1}.`);
        return false;
      }
      if (!ch.cheque_date) {
        message.warning(`Please select cheque date for Cheque #${i + 1}.`);
        return false;
      }
    }

    return true;
  };

  const buildPaymentsPayload = () => {
    const cashMethod = paymentMethods.find(
      (pm: any) => pm.code === getPaymentMethodCode(PaymentMethod.CASH),
    );
    const cardMethod = paymentMethods.find(
      (pm: any) => pm.code === getPaymentMethodCode(PaymentMethod.CARD),
    );
    const chequeMethod = paymentMethods.find(
      (pm: any) => pm.code === getPaymentMethodCode(PaymentMethod.CHEQUE),
    );
    const transferMethod = paymentMethods.find(
      (pm: any) =>
        pm.code === getPaymentMethodCode(PaymentMethod.BANK_TRANSFER),
    );
    const creditMethod = paymentMethods.find(
      (pm: any) => pm.code === getPaymentMethodCode(PaymentMethod.CREDIT),
    );

    const paymentsPayload: any[] = [];

    if (!isCreditSale) {
      if (cashAmount > 0 && cashMethod) {
        paymentsPayload.push({
          payment_method_id: cashMethod.id,
          amount: cashAmount,
          note: "Cash payment",
        });
      }

      if (cardAmount > 0 && cardMethod) {
        paymentsPayload.push({
          payment_method_id: cardMethod.id,
          amount: cardAmount,
          note: "Card payment",
          bank_account_id: cardBankAccountId,
          transaction_no: cardTxNo || null,
        });
      }

      if (transferAmount > 0 && transferMethod) {
        paymentsPayload.push({
          payment_method_id: transferMethod.id,
          amount: transferAmount,
          note: "Bank transfer payment",
          bank_account_id: transferBankAccountId,
          transaction_no: transferTxNo || null,
        });
      }

      if (cheques.length > 0 && chequeMethod) {
        cheques.forEach((ch) => {
          paymentsPayload.push({
            payment_method_id: chequeMethod.id,
            amount: ch.amount,
            note: `Cheque payment - No: ${ch.cheque_no}`,
            cheque_no: ch.cheque_no,
            bank_name: ch.bank_name,
            cheque_date: ch.cheque_date,
            bank_account_id: ch.bank_account_id || null,
          });
        });
      }
    }

    let mainPaymentMethodId = "";
    if (totalPaidAmount === 0) {
      mainPaymentMethodId = creditMethod?.id || cashMethod?.id || "";
    } else {
      let maxAmount = 0;
      let selectedMethodId = "";

      if (cashAmount > maxAmount && cashMethod) {
        maxAmount = cashAmount;
        selectedMethodId = cashMethod.id;
      }
      if (cardAmount > maxAmount && cardMethod) {
        maxAmount = cardAmount;
        selectedMethodId = cardMethod.id;
      }
      if (transferAmount > maxAmount && transferMethod) {
        maxAmount = transferAmount;
        selectedMethodId = transferMethod.id;
      }
      const maxCheque = cheques.reduce(
        (max, c) => (c.amount > max ? c.amount : max),
        0,
      );
      if (maxCheque > maxAmount && chequeMethod) {
        maxAmount = maxCheque;
        selectedMethodId = chequeMethod.id;
      }
      mainPaymentMethodId = selectedMethodId || cashMethod?.id || "";
    }

    return { paymentsPayload, mainPaymentMethodId };
  };

  const handleCheckout = useCallback(() => {
    if (cart.length === 0) {
      message.warning("Your cart is empty.");
      return;
    }

    if (paymentStatuses.length === 0) {
      message.error("Payment statuses not loaded yet.");
      return;
    }

    if (orderStatuses.length === 0) {
      message.error("Order statuses not loaded yet.");
      return;
    }

    // 1. Local validations
    if (!validate()) {
      return;
    }

    // 2. Credit check: if dueAmount > 0 or it's a Credit Sale, customer must be selected
    if ((isCreditSale || dueAmount > 0) && !customer) {
      message.warning(
        isCreditSale
          ? "Customer must be selected for Credit Sale transactions."
          : "Customer must be selected for credit (due balance) orders.",
      );
      return;
    }

    // 3. Credit limit enforcement – only when customer has a configured limit
    
    if (
      customer &&
      customer.credit_limit > 0 &&
      (isCreditSale || dueAmount > 0)
    ) {
      const outstandingBalance = (customerBalanceData as any)?.balance ?? 0;
      const newDue = isCreditSale ? grandTotal : dueAmount;
      const projectedBalance = outstandingBalance + newDue;
      const limit = customer.credit_limit!;

      if (projectedBalance > limit) {
        message.warning(
          `Credit limit exceeded for ${customer.name}. ` +
            `Current outstanding: Rs. ${outstandingBalance.toFixed(2)}, ` +
            `New due: Rs. ${newDue.toFixed(2)}, ` +
            `Total would be: Rs. ${projectedBalance.toFixed(2)}, ` +
            `Limit: Rs. ${limit.toFixed(2)}.`,
        );
        return;
      }
    }

    // 3. Build payments payload and retrieve primary payment method ID
    const { paymentsPayload, mainPaymentMethodId } = buildPaymentsPayload();

    // 4. Match payment status
    let targetPaymentStatusCode = getPaymentStatusCode(PaymentStatus.UNPAID);
    const actualPaidAmount = Math.min(totalPaidAmount, grandTotal);
    if (actualPaidAmount >= grandTotal) {
      targetPaymentStatusCode = getPaymentStatusCode(PaymentStatus.PAID);
    } else if (actualPaidAmount > 0) {
      targetPaymentStatusCode = getPaymentStatusCode(
        PaymentStatus.PARTIALLY_PAID,
      );
    }

    const matchedPaymentStatus = paymentStatuses.find(
      (ps: any) => ps.code === targetPaymentStatusCode,
    );
    if (!matchedPaymentStatus) {
      message.error(
        `Payment status with code "${targetPaymentStatusCode}" not found.`,
      );
      return;
    }
    const paymentStatusId = matchedPaymentStatus.id;

    const matchedOrderStatus = orderStatuses.find(
      (os: any) => os.code === getOrderStatusCode(OrderStatus.ORDERED),
    );
    if (!matchedOrderStatus) {
      message.error('Order status with code "ordered" not found.');
      return;
    }
    const orderStatusId = matchedOrderStatus.id;

    // Map cart items
    const orderItems = cart.map((item) => {
      const stock = item.rawProduct?.stocks?.find(
        (s: any) => s.batch_no === item.batch_no,
      );
      const isTemp = !!(
        item.is_tempory_item || item.rawProduct?.is_tempory_item
      );
      const listPrice = isWholeSale
        ? (item.rawProduct?.wholesale_price ?? item.price)
        : (item.rawProduct?.price ?? item.price);
      const perUnitDiscount = item.qty > 0 ? (item.discount_amount ?? 0) / item.qty : 0;
      const actualSoldPrice = item.price - perUnitDiscount;
      return {
        product_id: String(item.id),
        stock_id: stock ? String(stock.id) : null,
        quantity: item.qty,
        price: listPrice,
        sold_price: Math.max(0, actualSoldPrice),
        total_price: Math.max(0, actualSoldPrice * item.qty),
        discount_amount: item.discount_amount ?? 0.0,
        cost_price: isTemp ? item.cost_price : null,
        is_tempory_item: isTemp,
        item_name: isTemp ? item.name : null,
        description: item.description || null,
      };
    });

    const payload: any = {
      customer_id: customer?.id ? String(customer.id) : null,
      table_id: table?.id ? String(table.id) : null,
      selling_type: isWholeSale ? SellingType.WHOLESALE : SellingType.RETAIL,
      subtotal: cartTotal,
      discount_amount: discount,
      exchange_amount: exchangeDiscount,
      other_charges: otherCharges,
      tax_amount: 0.0,
      grand_total: grandTotal,
      paid_amount: actualPaidAmount,
      due_amount: dueAmount,
      payment_method_id: mainPaymentMethodId,
      payment_status_id: paymentStatusId,
      order_status_id: orderStatusId,
      employee_id: employeeId || null,
      items: orderItems,
      payments: paymentsPayload,
      repair_job_id: repairJobId,
      online_order_id: onlineOrderId,
    };

    if (editingOrderId) {
      payload.id = editingOrderId;
    }

    const mutation = payload.id ? updateOrderMutation : createOrderMutation;
    mutation.mutate(payload, {
      onSuccess: async (res) => {
        if (res.success) {
          message.success(res.message || "Order processed successfully!");
          const orderId = res.id;
          if (editingDraftId) {
            try {
              await DeleteDraftCart(editingDraftId, true);
            } catch (e) {
              console.error("Failed to delete draft after checkout:", e);
            }
          }
          clearCart();
          handleReset();
          onClose();
          if (onCheckoutSuccess && orderId) {
            onCheckoutSuccess(orderId);
          }
        } else {
          message.error(res.message || "Failed to process order.");
        }
      },
      onError: (err: any) => {
        message.error(err?.message || "An unexpected error occurred.");
      },
    });
  }, [
    cart,
    cartTotal,
    customer,
    customerBalanceData,
    table,
    isWholeSale,
    editingOrderId,
    paymentStatuses,
    orderStatuses,
    discount,
    otherCharges,
    employeeId,
    grandTotal,
    totalPaidAmount,
    dueAmount,
    createOrderMutation,
    updateOrderMutation,
    cashAmount,
    cardAmount,
    transferAmount,
    cheques,
    cardBankAccountId,
    transferBankAccountId,
    cardTxNo,
    transferTxNo,
    isCreditSale,
    paymentMethods,
    editingDraftId,
    clearCart,
    handleReset,
    onClose,
    onCheckoutSuccess,
  ]);

  // Keyboard shortcut definitions for confirming sale
  useKeyboardShortcuts(
    [
      {
        shortcut: SHORTCUTS.CHECKOUT.CONFIRM_SALE,
        options: {
          enabled: open,
          preventDefault: false,
          stopPropagation: true,
        },
        handler: (e) => {
          const activeEl = document.activeElement;
          if (activeEl && activeEl.getAttribute("aria-expanded") === "true") {
            return;
          }
          e.preventDefault();
          const now = Date.now();
          const delay = now - lastEnterTimeRef.current;
          if (delay < 500) {
            lastEnterTimeRef.current = 0;
            handleCheckout();
          } else {
            lastEnterTimeRef.current = now;
            message.info("Press Enter again to confirm sale");
          }
        },
      },
    ],
    { enabled: open },
  );

  const getLabelForCode = (code: string, fallback: string) => {
    switch (code) {
      case getPaymentMethodCode(PaymentMethod.CASH):
        return "Cash Payment";
      case getPaymentMethodCode(PaymentMethod.CARD):
        return "Card Payment";
      case getPaymentMethodCode(PaymentMethod.BANK_TRANSFER):
        return "Bank Transfer";
      case getPaymentMethodCode(PaymentMethod.CHEQUE):
        return "Cheque Payment";
      default:
        return fallback;
    }
  };

  const paymentModeOptions = [
    ...paymentMethods
      .filter(
        (pm: any) => pm.code !== getPaymentMethodCode(PaymentMethod.CREDIT),
      )
      .map((pm: any) => ({
        value: pm.code,
        label: getLabelForCode(pm.code, pm.name),
      })),
    { value: "split", label: "Split Payment (Multiple)" },
  ];

  const isCheckoutLoading =
    loadingPaymentMethods ||
    loadingPaymentStatuses ||
    loadingOrderStatuses ||
    loadingBankAccounts ||
    loadingEmployees ||
    loadingCustomers ||
    (!!editingOrderId && loadingOrderDetails);

  return {
    cartTotal,
    discount,
    setDiscount,
    otherCharges,
    setOtherCharges,
    employeeId,
    setEmployeeId,
    customer,
    setCustomer,
    grandTotal,
    balance,
    dueAmount,
    isPaid,
    isCheckoutLoading,
    checkoutMutation,
    handleCheckout,
    handleReset,
    editingOrderId,
    exchangeDiscount,

    // Payment Section States & Handlers
    cashAmount,
    setCashAmount,
    cardAmount,
    setCardAmount,
    cardTxNo,
    setCardTxNo,
    cardBankAccountId,
    setCardBankAccountId,
    transferAmount,
    setTransferAmount,
    transferTxNo,
    setTransferTxNo,
    transferBankAccountId,
    setTransferBankAccountId,
    cheques,
    setCheques,
    paymentMode,
    isCreditSale,
    bankAccounts,
    paymentModeOptions,
    cashAmountRef,
    cardAmountRef,
    transferAmountRef,
    chequeAmountRef,
    handleCreditSaleChange,
    handlePaymentModeChange,

    employees,
    customers,
    loadingCustomers,
  };
};
